import { h } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useAdParameters } from '../../common/creative/hooks/useAdParameters';
import { useAdSize } from '../../common/creative/hooks/useAdSize';
import { useClickthru } from '../../common/creative/hooks/useClickthru';
import { useHideVolumeOnEnd } from '../../common/creative/hooks/useHideVolumeOnEnd';
import { useOnTime } from '../../common/creative/hooks/useOnTime';
import { usePlatform } from '../../common/creative/hooks/usePlatform';
import { useSetAdBackgroundColor } from '../../common/creative/hooks/useSetAdBackgroundColor';
import { useSetAdIsDisplay } from '../../common/creative/hooks/useSetAdIsDisplay';
import { useSetVideoSlotVolume } from '../../common/creative/hooks/useSetVideoSlotVolume';
import { useStopAdOnVideoEnd } from '../../common/creative/hooks/useStopAdOnVideoEnd';
import { useUpdateTime } from '../../common/creative/hooks/useUpdateTime';
import { useVideoSlotPlayPause } from '../../common/creative/hooks/useVideoSlotPlayPause';
import { useVPAIDPlayState } from '../../common/creative/hooks/useVPAIDPlayState';
import { PerformanceSingleVideoConfig } from './config';
import { PerformanceSingleVideoCreative } from './creative';
import { Layout, useLayout } from './layout';

const assetRatio = 16 / 9;

export function PerformanceSingleVideo({ videoslot }: { videoslot: HTMLVideoElement }) {
  const adParameters = useAdParameters<PerformanceSingleVideoConfig>();

  const platform = usePlatform();
  const adSize = useAdSize();

  const titleRef = useRef<HTMLDivElement>(null);
  const [titleHasOneLine, setTitleHasOneLine] = useState(true);

  useLayoutEffect(() => {
    if (!titleRef.current || titleRef.current.offsetHeight < 1) return;

    const style = getComputedStyle(titleRef.current);
    setTitleHasOneLine(titleRef.current.offsetHeight < parseFloat(style.lineHeight) * 1.5);
  }, [titleRef.current?.innerHTML, titleRef.current?.offsetHeight]);

  const layout = useLayout(platform, adSize.width, titleHasOneLine);

  const { playState, stopVPAID } = useVPAIDPlayState();
  useVideoSlotPlayPause(playState, videoslot);

  useStopAdOnVideoEnd(videoslot, stopVPAID);
  useUpdateTime(videoslot);
  useSetVideoSlotVolume(videoslot);
  useHideVolumeOnEnd(videoslot);
  useSetAdBackgroundColor('#fff');
  useSetAdIsDisplay(false);

  const [ctaActive, setCtaActive] = useState(false);
  useOnTime(2, () => setCtaActive(true));

  const onClick = useClickthru(adParameters.config.destination_url);

  if (layout === Layout.Unknown) return null;

  return (
    <PerformanceSingleVideoCreative
      videoRatio={assetRatio}
      onClick={onClick}
      isCtaActive={ctaActive}
      videoslot={videoslot}
      layout={layout}
      titleRef={titleRef}
    />
  );
}
